import { Notification, NotificationType } from '../types';

class NotificationService {
  private listeners: ((notifications: Notification[]) => void)[] = [];
  private notifications: Notification[] = [];

  // Subscribe to notification updates
  subscribe(callback: (notifications: Notification[]) => void) {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  private notify() {
    this.listeners.forEach(callback => callback([...this.notifications]));
  }

  // Show a notification
  show(message: string, type: NotificationType = 'info', duration: number = 5000) {
    const notification: Notification = {
      id: this.generateId(),
      message,
      type,
      duration,
      timestamp: new Date().toISOString(),
    };

    this.notifications.push(notification);
    this.notify();

    // Auto-remove notification after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(notification.id);
      }, duration);
    }

    return notification.id;
  }

  // Remove a notification
  remove(id: string) {
    this.notifications = this.notifications.filter(notification => notification.id !== id);
    this.notify();
  }

  // Clear all notifications
  clear() {
    this.notifications = [];
    this.notify();
  }

  // Get all notifications
  getAll(): Notification[] {
    return [...this.notifications];
  }

  // Convenience methods for different types
  success(message: string, duration: number = 3000) {
    return this.show(message, 'success', duration);
  }

  error(message: string, duration: number = 8000) {
    return this.show(message, 'error', duration);
  }

  warning(message: string, duration: number = 5000) {
    return this.show(message, 'warning', duration);
  }

  info(message: string, duration: number = 5000) {
    return this.show(message, 'info', duration);
  }

  // Generate unique ID
  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Show loading notification that can be updated
  showLoading(message: string): string {
    return this.show(`🔄 ${message}`, 'info', 0); // Duration 0 means it won't auto-remove
  }

  // Update a notification (useful for loading states)
  update(id: string, message: string, type?: NotificationType) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.message = message;
      if (type) {
        notification.type = type;
      }
      this.notify();
    }
  }

  // Show progress notification
  showProgress(message: string, progress: number): string {
    const progressMessage = `${message} (${Math.round(progress)}%)`;
    return this.show(progressMessage, 'info', 0);
  }

  // Update progress notification
  updateProgress(id: string, message: string, progress: number) {
    const progressMessage = `${message} (${Math.round(progress)}%)`;
    this.update(id, progressMessage);
  }

  // Complete a progress notification
  completeProgress(id: string, message: string, success: boolean = true) {
    const icon = success ? '✅' : '❌';
    const type = success ? 'success' : 'error';
    this.update(id, `${icon} ${message}`, type);
    
    // Auto-remove after 3 seconds
    setTimeout(() => {
      this.remove(id);
    }, 3000);
  }

  // Show notification with custom icon
  showWithIcon(icon: string, message: string, type: NotificationType = 'info', duration: number = 5000) {
    return this.show(`${icon} ${message}`, type, duration);
  }

  // Batch operations
  showMultiple(notifications: Array<{ message: string; type?: NotificationType; duration?: number }>) {
    const ids: string[] = [];
    
    notifications.forEach((notif, index) => {
      // Stagger notifications slightly
      setTimeout(() => {
        const id = this.show(
          notif.message, 
          notif.type || 'info', 
          notif.duration || 5000
        );
        ids.push(id);
      }, index * 200);
    });
    
    return ids;
  }

  // Show confirmation-style notification (doesn't auto-remove)
  showConfirmation(message: string, type: NotificationType = 'info') {
    return this.show(message, type, 0);
  }

  // Analytics and debugging
  getStats() {
    return {
      total: this.notifications.length,
      byType: this.notifications.reduce((acc, notif) => {
        acc[notif.type] = (acc[notif.type] || 0) + 1;
        return acc;
      }, {} as Record<NotificationType, number>),
      listeners: this.listeners.length,
    };
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Export some common notification patterns
export const showApiError = (error: any, context?: string) => {
  const message = error?.response?.data?.detail || 
                 error?.response?.data?.message || 
                 error?.message || 
                 'An unexpected error occurred';
  
  const fullMessage = context ? `${context}: ${message}` : message;
  return notificationService.error(fullMessage);
};

export const showApiSuccess = (message: string, context?: string) => {
  const fullMessage = context ? `${context}: ${message}` : message;
  return notificationService.success(fullMessage);
};

export const showLoadingOperation = (operation: string) => {
  return notificationService.showLoading(`${operation}...`);
};

export const completeOperation = (id: string, operation: string, success: boolean = true) => {
  const message = success ? `${operation} completed successfully` : `${operation} failed`;
  notificationService.completeProgress(id, message, success);
};
